<template>
  <div class="w-full">
    <form class="space-y-5" @submit.prevent>
      
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 w-1 bg-indigo-500 rounded-l-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
        <textarea 
          id="url" 
          v-model="inputString" 
          rows="8" 
          spellcheck="false"
          class="block w-full p-4 pl-5 text-sm font-mono text-slate-800 bg-slate-50 rounded-xl border border-slate-200 shadow-inner placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-200 resize-y" 
          placeholder="Paste your string, JSON, JWT, or URL here..."
        ></textarea>
      </div>

      <div class="flex flex-col sm:flex-row gap-4 items-stretch">
        
        <div class="w-full sm:w-1/3 relative">
          <select 
            v-model="formatType" 
            class="block w-full h-full min-h-[48px] px-4 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl shadow-sm appearance-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer"
          >
            <option value="utf8">UTF-8 (URL)</option>
            <option value="base64">Base64 (Image/Text)</option>
            <option value="json">JSON (Minify/Format)</option>
            <option value="html">HTML Entities</option>
            <option value="jwt">JWT Payload</option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
          </div>
        </div>

        <div class="flex gap-3 w-full sm:w-2/3">
          <button 
            :disabled="formatType === 'jwt'"
            :class="formatType === 'jwt' ? 'opacity-50 cursor-not-allowed bg-slate-400' : 'bg-indigo-600 hover:bg-indigo-700'"
            class="flex-1 flex items-center justify-center gap-2 text-white text-sm font-bold py-3 px-4 rounded-xl shadow-sm transition-all duration-200 active:scale-[0.98]" 
            @click="processEncode"
          >
            <svg class="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
            {{ formatType === 'json' ? 'Minify' : 'Encode' }}
          </button>
          
          <button 
            class="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-3 px-4 rounded-xl shadow-sm transition-all duration-200 active:scale-[0.98]" 
            @click="processDecode"
          >
            <svg class="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path></svg>
            {{ formatType === 'json' ? 'Prettify' : 'Decode' }}
          </button>
        </div>

      </div>
    </form>

    <div class="mt-8 pt-4 border-t border-slate-100 flex items-center justify-center">
      <p class="text-xs text-slate-400 font-medium tracking-wide">
        Engineered by 
        <a 
          href="https://bonthatthana.netlify.app" 
          target="_blank" 
          rel="noopener noreferrer"
          class="text-indigo-500 hover:text-indigo-600 font-bold ml-1 transition-colors hover:underline underline-offset-2"
        >
          Bon Thatthana
        </a>
      </p>
    </div> 
  </div>
</template>

<script>
export default {
  name: 'EncodeDecode',
  data() {
    return {
      inputString: '',
      formatType: 'utf8' 
    }
  },
  methods: {
    processEncode() {
      if (!this.inputString.trim()) return alert('Please enter a data string first.');
      
      try {
        switch (this.formatType) {
          case 'utf8':
            this.inputString = encodeURIComponent(this.inputString);
            break;
          case 'base64':
            this.inputString = btoa(this.inputString);
            break;
          case 'json':
            // Parses JSON and spits it back out with zero spacing (Minify)
            this.inputString = JSON.stringify(JSON.parse(this.inputString));
            break;
          case 'html':
            // Creates a native DOM element to safely encode structural tags
            const div = document.createElement('div');
            div.innerText = this.inputString;
            this.inputString = div.innerHTML;
            break;
          case 'jwt':
            alert('JWT encoding requires a secure server key. You can only decode JWTs here.');
            break;
        }
      } catch (err) {
        alert(`Encoding Error: Check your ${this.formatType.toUpperCase()} format syntax.`);
      }
    },

    processDecode() {
      if (!this.inputString.trim()) return alert('Please enter a data string first.');

      try {
        switch (this.formatType) {
          case 'utf8':
            this.inputString = decodeURIComponent(this.inputString);
            break;
          case 'base64':
            this.inputString = atob(this.inputString);
            break;
          case 'json':
            // Parses JSON and formats it with a 2-space indentation (Prettify)
            this.inputString = JSON.stringify(JSON.parse(this.inputString), null, 2);
            break;
          case 'html':
            // Reads encoded entities and strips them back to raw characters
            const doc = new DOMParser().parseFromString(this.inputString, "text/html");
            this.inputString = doc.documentElement.textContent;
            break;
          case 'jwt':
            // Extracts the middle payload block from the JWT and formats it into readable JSON
            const base64Url = this.inputString.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            this.inputString = JSON.stringify(JSON.parse(jsonPayload), null, 2);
            break;
        }
      } catch (err) {
        alert(`Decoding Error: This is not a valid ${this.formatType.toUpperCase()} string.`);
      }
    }
  }
}
</script>